/**
 * Enhanced Interview Content Component
 * Handles the main content area with optimized rendering
 */

import React, { memo, Suspense } from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import InterviewCardSkeleton from './InterviewCardSkeleton';
import InterviewCalendar from './InterviewCalendar';

// Lazy load heavy components
const LazyInterviewList = React.lazy(() => 
  import('./LazyInterviewList').catch(() => ({ 
    default: () => <div className="p-4 text-center text-text-secondary">Failed to load interview list</div> 
  }))
);

// Loading fallback for list
const ListLoadingFallback = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[...Array(6)].map((_, index) => (
      <InterviewCardSkeleton key={index} />
    ))}
  </div>
);

// Empty state component
const EmptyInterviewsState = memo(({ 
  activeTab, 
  onScheduleInterview,
  hasFilters 
}) => (
  <div className="p-8 text-center border border-border rounded-lg">
    <div className="mx-auto w-16 h-16 bg-background rounded-full flex items-center justify-center mb-4">
      <Icon name="Calendar" size={24} className="text-text-secondary" />
    </div>
    <h3 className="text-lg font-medium mb-2">No interviews found</h3>
    <p className="text-text-secondary max-w-md mx-auto mb-6">
      {hasFilters 
        ? `No interviews match your current filters. Try adjusting your search criteria.`
        : activeTab === 'all' 
          ? "You don't have any interviews yet. Schedule your first interview to get started."
          : `You don't have any ${activeTab} interviews.`
      }
    </p>
    {!hasFilters && (
      <Button 
        variant="primary" 
        onClick={onScheduleInterview}
      >
        Schedule an Interview
      </Button>
    )}
  </div>
));

EmptyInterviewsState.displayName = 'EmptyInterviewsState';

// Error state component
const ErrorInterviewsState = memo(({ onRetry }) => (
  <div className="p-8 text-center">
    <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <Icon name="AlertTriangle" size={24} className="text-red-600" />
    </div>
    <h3 className="text-lg font-medium mb-2">Unable to load interviews</h3>
    <p className="text-text-secondary mb-6">
      There was a problem loading your interviews. Please try again.
    </p>
    <Button 
      variant="outline" 
      onClick={onRetry}
      className="flex items-center gap-2"
    >
      <Icon name="RefreshCw" size={16} />
      Retry
    </Button>
  </div>
));

ErrorInterviewsState.displayName = 'ErrorInterviewsState';

const InterviewManagementContent = memo(({
  // Calendar props
  calendarView,
  calendarEvents,
  onCalendarInterviewClick,
  onCalendarDateClick,
  
  // List props
  interviews,
  loading,
  error,
  activeTab,
  viewMode,
  hasActiveFilters,
  
  // Handlers
  onScheduleInterview,
  onReschedule,
  onCancel,
  onComplete,
  onMessageCandidate,
  onStatusChange,
  onRetry
}) => {
  // Calendar View
  if (calendarView) {
    return (
      <div className="mb-6 w-full py-4">
        <Suspense fallback={<div className="h-96 bg-gray-100 rounded-lg animate-pulse" />}>
          <InterviewCalendar
            interviews={interviews}
            events={calendarEvents}
            onInterviewClick={onCalendarInterviewClick}
            onDateClick={onCalendarDateClick}
            viewType="month"
          />
        </Suspense>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return <ListLoadingFallback />;
  }

  // Error State
  if (error) {
    return <ErrorInterviewsState onRetry={onRetry} />;
  }

  // Empty State
  if (interviews.length === 0) {
    return (
      <EmptyInterviewsState 
        activeTab={activeTab}
        onScheduleInterview={onScheduleInterview}
        hasFilters={hasActiveFilters}
      />
    );
  }

  // Interview List
  return (
    <div>
      <Suspense fallback={<ListLoadingFallback />}>
        <LazyInterviewList 
          interviews={interviews}
          viewMode={viewMode}
          onReschedule={onReschedule}
          onCancel={onCancel}
          onComplete={onComplete}
          onMessage={onMessageCandidate}
          onStatusChange={onStatusChange}
        />
      </Suspense>
    </div>
  );
});

InterviewManagementContent.displayName = 'InterviewManagementContent';

export default InterviewManagementContent;